"use client";

import { useEffect } from "react";
import posthog from "posthog-js";
import { readLocal, writeLocal } from "@/helpers/safeStorage";

/**
 * Sonde temporaire avant la migration Tailwind v4.
 *
 * Tailwind v4 repose sur trois fondations CSS que les navigateurs d'avant
 * mars 2023 n'ont pas (Safari < 16.4, Chrome < 111, Firefox < 128) : les
 * couleurs oklch/color-mix, la règle @property et les cascade layers. Sur un
 * navigateur qui ne les a pas, la feuille de style ne se dégrade pas
 * gracieusement : couleurs et espacements disparaissent.
 *
 * Plutôt que d'estimer ce trafic depuis des statistiques publiques, on mesure
 * sur nos propres visiteurs : un événement par appareil et par jour, avec le
 * verdict de chaque fondation. La décision de migrer se lira dans PostHog
 * (insight sur `tailwind4_capability`, part de `supports_tailwind4: false`).
 *
 * À retirer une fois la décision prise.
 */
const CLE_DEDUP = "tw4_probe_day";

export default function Tailwind4CapabilityProbe() {
    useEffect(() => {
        try {
            const aujourdHui = new Date().toISOString().slice(0, 10);
            if (readLocal(CLE_DEDUP) === aujourdHui) return;

            const supportsOklch =
                typeof CSS !== "undefined" &&
                CSS.supports("color", "oklch(0.5 0.2 200)");
            const supportsColorMix =
                typeof CSS !== "undefined" &&
                CSS.supports("color", "color-mix(in oklab, red, blue)");
            const supportsAtProperty = typeof CSSPropertyRule !== "undefined";
            const supportsLayers = typeof CSSLayerBlockRule !== "undefined";

            posthog.capture("tailwind4_capability", {
                supports_oklch: supportsOklch,
                supports_color_mix: supportsColorMix,
                supports_at_property: supportsAtProperty,
                supports_cascade_layers: supportsLayers,
                supports_tailwind4:
                    supportsOklch &&
                    supportsColorMix &&
                    supportsAtProperty &&
                    supportsLayers,
            });

            writeLocal(CLE_DEDUP, aujourdHui);
        } catch {
            // Une sonde ne casse jamais la page.
        }
    }, []);

    return null;
}
